import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, Clock, MapPin, Phone, Truck, Home, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CustomerLayout } from '@/components/layout/CustomerLayout';
import { getOrder } from '@/lib/api';
import { getTokenForOrder } from '@/lib/orderHistory';

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        // Use the device-bound token if we have one (full details).
        // Otherwise the API returns a redacted, privacy-safe view.
        const token = getTokenForOrder(orderId);
        const response = await getOrder(orderId, token);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <CustomerLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </CustomerLayout>
    );
  }

  if (!order) {
    return (
      <CustomerLayout>
        <div className="px-4 py-12 max-w-md mx-auto text-center">
          <h2 className="text-xl font-bold mb-4">Order not found</h2>
          <Link to="/">
            <Button className="rounded-full">Go Home</Button>
          </Link>
        </div>
      </CustomerLayout>
    );
  }

  const statusSteps = [
    { key: 'pending', label: 'Order Placed', icon: CheckCircle2 },
    { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2 },
    { key: 'preparing', label: 'Preparing', icon: Clock },
    { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
    { key: 'delivered', label: 'Delivered', icon: Home },
  ];

  const currentStatusIndex = statusSteps.findIndex(s => s.key === order.status);

  return (
    <CustomerLayout>
      <div className="px-4 py-6 max-w-2xl mx-auto" data-testid="order-confirmation">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-primary mb-2">Order Placed!</h1>
          <p className="text-muted-foreground">
            Your order #{order.order_number} has been placed successfully
          </p>
        </div>

        {/* Delivery Time */}
        <div className="bg-primary/10 rounded-xl p-4 flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-semibold">Estimated Delivery</p>
            <p className="text-lg font-bold text-primary">{order.estimated_delivery}</p>
          </div>
        </div>

        {/* Order Status */}
        <div className="bg-white rounded-xl border border-border p-4 mb-6">
          <h2 className="font-semibold mb-4">Order Status</h2>
          <div className="flex items-center justify-between">
            {statusSteps.slice(0, 4).map((step, index) => (
              <div key={step.key} className="flex flex-col items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    index <= currentStatusIndex 
                      ? 'bg-primary text-white' 
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <step.icon className="w-4 h-4" />
                </div>
                <span className={`text-xs mt-1 ${
                  index <= currentStatusIndex ? 'text-primary font-medium' : 'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Details — only shown to the customer who placed the order
            (i.e., when this device holds a valid view_token). Public viewers
            of the URL see a privacy notice instead. */}
        {order.customer_name ? (
          <div className="bg-white rounded-xl border border-border p-4 mb-6">
            <h2 className="font-semibold mb-4">Delivery Details</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{order.customer_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {order.address}
                    {order.pincode ? ` — ${order.pincode}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-muted-foreground" />
                <p className="text-sm">{order.phone}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6 flex items-start gap-3">
            <Lock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm">Customer details are private</p>
              <p className="text-xs text-muted-foreground mt-1">
                Delivery name, phone and address are visible only on the device that placed this order.
              </p>
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="bg-white rounded-xl border border-border p-4 mb-6">
          <h2 className="font-semibold mb-4">Order Items</h2>
          <div className="space-y-3">
            {order.items.map((item) => (
              <div key={item.product_id} className="flex justify-between text-sm">
                <span>
                  {item.name} ({item.unit}) x {item.quantity}
                </span>
                <span className="font-medium">₹{item.subtotal.toFixed(0)}</span>
              </div>
            ))}
            <div className="border-t border-border pt-3 flex justify-between font-bold">
              <span>Total ({order.payment_method === 'cod' ? 'COD' : 'UPI'})</span>
              <span className="text-primary">₹{order.total.toFixed(0)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/my-orders" className="flex-1" data-testid="view-my-orders-btn">
            <Button variant="outline" className="w-full h-12 rounded-full">
              View My Orders
            </Button>
          </Link>
          <Link to="/" className="flex-1" data-testid="continue-shopping-btn">
            <Button variant="outline" className="w-full h-12 rounded-full">
              Continue Shopping
            </Button>
          </Link>
          <a 
            href={`https://wa.me/919603540072?text=${encodeURIComponent(`Hi! I want to track my order #${order.order_number}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
            data-testid="track-whatsapp-btn"
          >
            <Button className="w-full h-12 rounded-full bg-[#25D366] hover:bg-[#25D366]/90">
              Track on WhatsApp
            </Button>
          </a>
        </div>
      </div>
    </CustomerLayout>
  );
}
